import { zodResolver } from '@hookform/resolvers/zod';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState, type FC } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { BsEnvelope, BsPerson } from 'react-icons/bs';
import {
  IoFemaleOutline,
  IoMaleFemaleOutline,
  IoMaleOutline,
} from 'react-icons/io5';
import { PiGlobeSimple } from 'react-icons/pi';
import { RiCake2Line } from 'react-icons/ri';
import { useLocation, useNavigate } from 'react-router';
import { z } from 'zod';

import {
  Button,
  Dropdown,
  LoadingOverlay,
  PhoneNumberInput,
  TextInput,
  toast,
} from '@/components';
import type { DropdownOption } from '@/components/Dropdown';
import type { PhoneNumberInputValue } from '@/components/PhoneNumberInput';
import { Gender } from '@/models';
import type { Gender as GenderType } from '@/models';
import BirthdaySelectionModalBottomSheet from '@/pages/components/bottomsheets/BirthdaySelectionModalBottomSheet';
import ProfileAvatar from '@/pages/components/ProfileAvatar';
import { BookingService } from '@/services/bookingService';
import { getLiffProfile } from '@/services/liffService';
import { ProfileService, useStoredProfile } from '@/services/profileService';
import { phoneSuperRefine } from '@/utils/validation';

interface ProfileSettingsForm {
  name: string;
  email: string;
  telPrefix: string;
  tel: string;
  nationalityCode: string;
  gender?: GenderType;
  birthday?: Date;
}

interface ProfileSettingsNavState {
  telPrefix?: string;
  tel?: string;
}

const validationSchema = z
  .object({
    name: z.string().trim().min(1, { message: 'validation.required' }),
    email: z
      .string()
      .trim()
      .min(1, { message: 'validation.required' })
      .email({ message: 'validation.invalidEmail' }),
    telPrefix: z.string().min(1),
    tel: z.string(),
    nationalityCode: z.string(),
    gender: z.nativeEnum(Gender).optional(),
    birthday: z.date().optional(),
  })
  .superRefine(phoneSuperRefine('tel', 'telPrefix', '+66', false));

const ProfileSettingsPage: FC = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const navState = (location.state ?? {}) as ProfileSettingsNavState;

  const isLoggedIn = ProfileService.isLoggedIn();
  const requiresSignupFlow = !isLoggedIn;

  const stored = useStoredProfile();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<ProfileSettingsForm>({
    resolver: zodResolver(validationSchema),
    defaultValues: {
      name: '',
      email: '',
      telPrefix: '+66',
      tel: '',
      nationalityCode: '',
      gender: undefined,
      birthday: undefined,
    },
  });

  useEffect(() => {
    if (!isLoggedIn) {
      reset({
        name: '',
        email: '',
        telPrefix: navState.telPrefix ?? '+66',
        tel: navState.tel ?? '',
        nationalityCode: '',
        gender: undefined,
        birthday: undefined,
      });
      return;
    }

    if (!stored) return;

    reset({
      name: stored.name ?? '',
      email: stored.email ?? '',
      telPrefix: stored.telPrefix ?? '+66',
      tel: stored.tel ?? '',
      nationalityCode: stored.nationalityCode ?? '',
      gender: stored.gender,
      birthday: stored.birthday ? new Date(stored.birthday) : undefined,
    });
  }, [isLoggedIn, navState.tel, navState.telPrefix, reset, stored]);

  const genderOptions: DropdownOption<Gender>[] = [
    {
      value: Gender.Male,
      label: t('gender.male'),
      icon: <IoMaleOutline className="text-male size-6" />,
    },
    {
      value: Gender.Female,
      label: t('gender.female'),
      icon: <IoFemaleOutline className="text-female size-6" />,
    },
    {
      value: Gender.LGBTQIA,
      label: t('gender.lgbtqia'),
      icon: <img src="/icons/lgbtq.png" alt="LGBTQIA+" className="size-6" />,
    },
  ];

  const [nationalityOptions, setNationalityOptions] = useState<
    DropdownOption<string>[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [birthdayOpen, setBirthdayOpen] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [avatarSrc, setAvatarSrc] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const nationalities = await ProfileService.getNationalities();
      const options: DropdownOption<string>[] = nationalities.map(
        (c): DropdownOption<string> => ({ value: c.code, label: c.name }),
      );
      if (!cancelled) setNationalityOptions(options);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const liffProfile = await getLiffProfile();
      if (!cancelled && liffProfile?.pictureUrl) {
        setAvatarSrc(liffProfile.pictureUrl);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const telPrefix = watch('telPrefix') || '+66';
  const tel = watch('tel') || '';
  const nationalityCode = watch('nationalityCode');
  const gender = watch('gender');
  const birthday = watch('birthday');

  const birthdayLabel = useMemo(() => {
    if (!birthday) return '';
    const dayjsLocale = i18n.language.startsWith('th') ? 'th' : 'en';
    return dayjs(birthday).locale(dayjsLocale).format('DD MMM');
  }, [birthday, i18n.language]);

  const onSubmit = async (data: ProfileSettingsForm) => {
    setLoading(true);
    setErrorKey(null);

    try {
      const birthdayYmd = data.birthday
        ? ProfileService.toBirthdayYmd(data.birthday)
        : undefined;

      const profile = {
        name: data.name.trim(),
        telPrefix: data.telPrefix || '+66',
        tel: data.tel,
        email: data.email.trim(),
        nationalityCode: data.nationalityCode || undefined,
        gender: data.gender,
        birthday: birthdayYmd,
      };

      const hasStoredPhone = !!stored?.tel && !!stored?.telPrefix;
      const phoneChanged =
        !hasStoredPhone ||
        stored?.telPrefix !== profile.telPrefix ||
        stored?.tel !== profile.tel;

      // Signup requires OTP verification first.
      // If not logged in, always do OTP flow and let OTP page perform signup.
      if (requiresSignupFlow) {
        const destination = `${profile.telPrefix}${profile.tel}`;
        const resp = await BookingService.requestOTP(
          destination,
          'phoneNumber',
        );
        void navigate('/profile/otp', {
          state: {
            ref: resp.ref,
            phoneNumber: destination,
            telPrefix: profile.telPrefix,
            tel: profile.tel,
            devOtpCode: resp.code,
            expiresAt: resp.expiresAt,
            intent: 'saveProfile',
            profile,
          },
        });
        return;
      }

      if (phoneChanged) {
        const destination = `${profile.telPrefix}${profile.tel}`;
        const resp = await BookingService.requestOTP(
          destination,
          'phoneNumber',
        );
        void navigate('/profile/otp', {
          state: {
            ref: resp.ref,
            phoneNumber: destination,
            telPrefix: profile.telPrefix,
            tel: profile.tel,
            devOtpCode: resp.code,
            expiresAt: resp.expiresAt,
            intent: 'saveProfile',
            profile,
          },
        });
        return;
      }

      await ProfileService.updateMe({
        name: profile.name,
        email: profile.email,
        telPrefix: profile.telPrefix,
        tel: profile.tel,
        nationality: profile.nationalityCode,
        birthday: birthdayYmd,
        gender: profile.gender,
      });

      toast.success(t('profile.settings.toast.updated'));
      return;
    } catch (e) {
      console.error('Failed to save profile:', e);
      setErrorKey('profile.settings.errors.saveFailed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto flex w-full max-w-md flex-1 flex-col">
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-1 flex-col">
        <div className="flex flex-col gap-4">
          {requiresSignupFlow && (
            <h2 className="text-primary-text text-center text-xl leading-7 font-semibold">
              {t('profile.settings.signUp')}
            </h2>
          )}

          <div className="flex items-center justify-center py-2">
            <ProfileAvatar src={avatarSrc} size={80} />
          </div>

          <TextInput
            label={t('profile.settings.fullName')}
            placeholder={t('profile.settings.fullName')}
            required
            prefixIcon={<BsPerson className="size-6 text-gray-400" />}
            error={errors.name?.message ? t(errors.name.message) : undefined}
            {...register('name')}
          />

          <PhoneNumberInput
            label={t('profile.settings.phone')}
            placeholder={t('profile.settings.phone')}
            required
            telPrefix={telPrefix}
            value={tel}
            onChange={(val: PhoneNumberInputValue) => {
              setValue('telPrefix', val.dialCode, {
                shouldValidate: false,
                shouldDirty: true,
              });
              setValue('tel', val.national, {
                shouldValidate: true,
                shouldDirty: true,
              });
            }}
            error={errors.tel?.message ? t(errors.tel.message) : undefined}
          />

          <TextInput
            label={t('profile.settings.email')}
            placeholder={t('profile.settings.email')}
            type="email"
            required
            prefixIcon={<BsEnvelope className="size-6 text-gray-400" />}
            error={errors.email?.message ? t(errors.email.message) : undefined}
            {...register('email')}
          />

          <Dropdown
            placeholder={t('profile.settings.nationality')}
            options={nationalityOptions}
            value={nationalityCode}
            onChange={(value) => {
              setValue('nationalityCode', value as string, {
                shouldValidate: false,
                shouldDirty: true,
              });
            }}
            prefixIcon={<PiGlobeSimple className="size-6" />}
            error={
              errors.nationalityCode?.message
                ? t(errors.nationalityCode.message)
                : undefined
            }
          />

          <div className="grid grid-cols-2 gap-4">
            <Dropdown
              placeholder={t('profile.settings.gender')}
              options={genderOptions.map((o) => ({
                ...o,
                label: t(
                  o.value === Gender.Male
                    ? 'profile.settings.genders.male'
                    : o.value === Gender.Female
                      ? 'profile.settings.genders.female'
                      : 'profile.settings.genders.lgbtqia',
                ),
              }))}
              value={gender}
              onChange={(value) => {
                setValue('gender', value as Gender, {
                  shouldValidate: false,
                  shouldDirty: true,
                });
              }}
              prefixIcon={<IoMaleFemaleOutline className="size-6" />}
              error={
                errors.gender?.message ? t(errors.gender.message) : undefined
              }
            />

            <TextInput
              label={t('profile.settings.birthdate')}
              placeholder={t('profile.settings.birthdate')}
              value={birthdayLabel}
              readOnly
              onClick={() => setBirthdayOpen(true)}
              suffixIcon={<RiCake2Line className="size-6 text-gray-400" />}
            />
          </div>

          {errorKey && <p className="text-error text-sm">{t(errorKey)}</p>}
        </div>

        <div className="mt-auto px-4 pt-6">
          <div className="sticky bottom-0 -mx-4 px-0 pt-4 pb-0">
            <Button type="submit" variant="primary" disabled={loading}>
              {requiresSignupFlow
                ? t('profile.settings.signUp')
                : t('profile.settings.save')}
            </Button>
          </div>
        </div>
      </form>

      <BirthdaySelectionModalBottomSheet
        isOpen={birthdayOpen}
        onClose={() => setBirthdayOpen(false)}
        initialDate={birthday}
        onConfirm={(date) => {
          setValue('birthday', date, { shouldDirty: true });
        }}
      />

      <LoadingOverlay isVisible={loading} />
    </div>
  );
};

export default ProfileSettingsPage;
